<script lang="ts">
	import { devices_status } from 'src/stores/device-runtime/status';
	//Hooks
	import { beforeUpdate, onMount } from 'svelte';

	//Services
	import { format } from 'svelte-i18n';

	//Stores
	import {
		createMemberSettingsSnapshot,
		createMemberStatusSnapshot,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';

	//Helpers
	import {
		get_gen_2_component_channel_structure,
		get_mode,
		hasTilt,
		is_gen_2,
		is_gen_z,
		real_id,
		real_id_z,
	} from '../../../helpers/interface_helpers';
	import { roller_is_calibrated } from 'src/helpers/requests/calibrate_cover';

	//Design system components
	import { Slider, toast } from '@Shelly/shelly-design-system';
	import {
		handleCloudLocalLists,
		localGroupRequest,
		updateGroupPosition,
		cloudGroupRequestV2,
		getDevicesFormatted,
	} from './control_group_helpers';

	// Types
	import type { Device } from '../../../types/Device';
	import type { Group } from '../../../types/Group';
	import { get_local_status } from 'src/services/local_requests';
	import { update_settings } from 'src/helpers/cloud_disabled_helpers';
	import { capFirstChar } from 'src/helpers/utilities';

	export let group: Group;

	$: memberStatusStore = createMemberStatusSnapshot(group?.devices ?? []);
	$: memberSettingsStore = createMemberSettingsSnapshot(group?.devices ?? []);
	$: memberStatus = $memberStatusStore;
	$: memberSettings = $memberSettingsStore;

	let cloud = {} as Device,
		local = {} as Device,
		firstInList: Device,
		value: number,
		cachedValue = value,
		slatPosition: number,
		isLoading = false,
		hasSlat = false,
		mutualRollersState = 'stop';
	$: firstOnline = group?.devices?.findIndex(
		(id) => memberStatus?.[id]?.online && isCalibrated($devices[id], memberStatus[real_id(id)]),
	);

	interface _Group {
		id: string;
		mode: string;
		channel?: number;
		type?: string | number;
		gen?: number;
		category?: string;
	}

	const isCalibrated = (device: Device, status) => {
		const isGen2 = is_gen_2(device);
		if (device === undefined) {
			return false;
		}
		if (!isGen2) {
			return device.category === 'roller'
				? status.rollers?.[device.channel].positioning && memberStatus?.[device.id]?.online
				: false;
		} else {
			return (
				roller_is_calibrated(device, status, memberSettings[real_id(device.id)]) &&
				memberStatus?.[real_id(device.id)]?.online
			);
		}
	};
	let disabled;

	const getValue = () => {
		let _value;
		const isGen2 = is_gen_2(firstInList);
		// const isGenZ = is_gen_z(firstInList);
		if (mutualRollersState === 'stop' && !isLoading) {
			//getting value before update mostly, once on mount to prevent flickering to 0
			if (isGen2) {
				_value =
					memberStatus[real_id(firstInList?.id)]?.['cover:' + firstInList?.channel]
						?.current_pos;
				// } else if (isGenZ) {
				// 	value =
				// 		memberStatus[real_id(firstInList?.id])?.['Multilevel Switch:1')])
				// 			?.currentValue;
			} else {
				_value =
					memberStatus[real_id(firstInList?.id)]?.rollers?.[firstInList?.channel]?.current_pos;
			}
			if (typeof _value === 'undefined' || _value < 0) {
				_value = 0;
			}
			if (_value > 100) _value = 100;
			if (cachedValue !== _value) {
				value = _value;
				cachedValue = value;
			}
		}
	};

	const getState = () => {
		const group_devices: _Group[] = Object.values($devices)
			.filter(
				(d: Device): boolean =>
					group?.devices?.includes(d.id) &&
					typeof memberStatus[real_id(d.id)] !== 'undefined' &&
					memberSettings[real_id(d.id)] &&
					$devices[d.id].category == 'roller',
			)
			.map((d: { id }) => {
				return {
					mode: get_mode(d, memberStatus[real_id(d.id)], memberSettings[real_id(d.id)]),
					...d,
				};
			});

		for (const device of group_devices) {
			if (memberStatus[real_id(device.id)]?.online) {
				if (is_gen_2($devices[device.id])) {
					mutualRollersState = ['stopped', 'closed', 'open'].includes(
						memberStatus[real_id(device.id)]?.[
							`${get_gen_2_component_channel_structure(device)}`
						]?.state,
					)
						? 'stop'
						: memberStatus[real_id(device.id)]?.[
								`${get_gen_2_component_channel_structure(device)}`
							]?.state;
					// } else if (is_gen_z($devices[device.id])) {
					// 	let moving =
					// 		memberStatus[real_id(device.id])?.['Meter:1')])[
					// 			'value.Electric_W_Consumed/66049'
					// 		] !== 0;
					// 	if (moving) {
					// 		mutualRollersState = mutualRollersState;
					// 	} else {
					// 		setTimeout(() => {
					// 			if (!moving) {
					// 				mutualRollersState = 'stop';
					// 			}
					// 		}, 10000); //needed for faking direction control TODO: remove after firmware update with good statuses
					// 	}
				} else {
					/**
					 * mapping gen 1 to gen 2 statuses, it's easier :)
					 */
					mutualRollersState =
						memberStatus[real_id(device.id)]?.rollers?.[device.channel]?.state === 'open'
							? 'opening'
							: (memberStatus[real_id(device.id)]?.rollers?.[device.channel]).state ===
								  'close'
								? 'closing'
								: 'stop';
				}
			}
		}

		isLoading = mutualRollersState === 'opening' || mutualRollersState === 'closing';
	};

	// Helper to check if a device supports slat position
	const deviceHasSlat = (device: Device) => {
		if (typeof device === 'undefined') {
			return false;
		}

		return (
			hasTilt(device) &&
			memberSettings[real_id(device.id)]?.['cover:' + device?.channel]?.slat?.enable
		);
	};

	onMount(() => {
		if (!isLoading) {
			getState();

			firstInList = $devices[group.devices[firstOnline]];

			const values = [];
			for (const id of group.devices) {
				const device = $devices[id];
				const isGen2 = is_gen_2(device);

				if (typeof device !== 'undefined') {
					values.push(
						isGen2
							? memberStatus?.[device?.id]?.['cover:' + device.channel]?.current_pos
							: memberStatus[real_id(device.id)]?.rollers?.[device.channel]?.current_pos,
					);

					let _hasSlat = deviceHasSlat(device);

					if (_hasSlat) {
						hasSlat = true;
						slatPosition = memberStatus[real_id(id)]?.['cover:' + device?.channel]?.slat_pos;
					}
				}
			}
			value = values.filter((x) => typeof x !== 'undefined' && x < 101)[firstOnline];
			cachedValue = value;
		}
	});

	beforeUpdate(() => {
		const lists = handleCloudLocalLists(group.devices);
		[cloud, local] = [lists.cloud, lists.local];
		getState();
		// if (cachedValue !== value) {
		getValue();
		// }
		disabled =
			group.devices.findIndex((id) => isCalibrated($devices[id], memberStatus[real_id(id)])) ===
				-1 || firstOnline === -1;
	});

	const changePosition = async (e: CustomEvent, type = 'cover') => {
		isLoading = true;
		const isCover = type === 'cover';
		const isGenZ = Object.values(cloud).some((d) => is_gen_z($devices[real_id_z(d.id)]));

		const valueToSend = e.detail.value;

		let res;
		let data = {};
		if (isCover) {
			value = valueToSend;
			data = { position: valueToSend };
		} else {
			slatPosition = valueToSend;
			data = { slatPosition: valueToSend };
		}

		if (Object.keys(cloud).length > 0) {
			let tmpDevices = getDevicesFormatted(Object.values(cloud), data);

			// Filter out devices that do not have slat position
			if (!isCover && tmpDevices?.cover) {
				tmpDevices.cover.ids = tmpDevices.cover.ids.filter((id) => {
					return deviceHasSlat($devices[id]);
				});
			}

			res = await cloudGroupRequestV2(
				`/v2/devices/set/groups`,
				tmpDevices,
				data,
				isGenZ ? 12000 : undefined,
			);
		}
		if (typeof res !== 'undefined' && Object.keys(res?.failedCommands || {}).length === 0) {
			await updateGroupPosition(valueToSend, group);
		}

		if (Object.keys(local).length > 0) {
			for (const device of Object.values(local)) {
				const _is_gen_2 = is_gen_2(device);
				const status = memberStatus[real_id(device.id)];
				const settings = memberSettings[real_id(device.id)];
				const rollerCalibrated = roller_is_calibrated(device, status, settings);
				const rollerLostPosition = is_gen_2
					? status?.[`${get_gen_2_component_channel_structure(device)}`]?.current_pos === null
					: false;

				const positioningControl =
					!is_gen_2 && device.category === 'roller'
						? status.rollers?.[device.channel].positioning
						: true;
				if (rollerCalibrated && positioningControl && !rollerLostPosition) {
					const data = _is_gen_2
						? {
								id: device.channel,
								[isCover ? 'pos' : 'slat_pos']: valueToSend,
							}
						: { go: 'to_pos', roller_pos: valueToSend };
					if (is_gen_2) {
						res = await localGroupRequest(device, data, 'Cover.GoToPosition', group);
					} else {
						res = await localGroupRequest(device, data, 'post', group);
					}
					if (res.isok) {
						const status_req = await get_local_status(
							$devices[device.id],
							$devices[device.id].ip,
						);
						let newStatus = {
							...memberStatus[real_id(device.id)],
							...status_req,
						};
						devices_status.update_device_status({
							id: $devices[device.id].id,
							msg: newStatus,
						});
						await update_settings($devices[device.id]);
					} else {
						toast.open({ type: 'danger', title: $format('error_global') });
					}
				}
			}
		}
		if (typeof res === 'undefined' || !res?.isok) {
			setTimeout(() => {
				isLoading = false;
			}, 10000);
		} else if (res.isok) {
			isLoading = false;
		}
	};

	const a11yPropsPosition = {
		ariaLabel: $format('label_position', { default: 'Position' }),
		ariaLabelValue: capFirstChar(
			$format('current_position', {
				default: 'Current position',
			}) + `: ${value}`,
		),
	};

	const a11yPropsTilt = {
		ariaLabel: $format('label_blinds_tilt', { default: 'Blinds tilt' }),
		ariaLabelValue: capFirstChar(
			$format('current_blinds_tilt', {
				default: 'Current blinds tilt',
			}) + `: ${slatPosition}`,
		),
	};
</script>

{#if hasSlat}
	<div class="flex-full-height flex-direction-row mb-m">
		<Slider
			a11yProps={a11yPropsPosition}
			id="slider-gga22f9"
			class="d-flex flex-1 min-width-0"
			title={$format('label_position')}
			splitTitle
			{value}
			{disabled}
			type={'vertical'}
			on:value_change={async (e) => await changePosition(e)}
			{isLoading}
		/>
		<Slider
			a11yProps={a11yPropsTilt}
			id="slider-9i81nev"
			title={$format('label_blinds_tilt', { default: 'Blinds tilt' })}
			value={slatPosition}
			{disabled}
			splitTitle
			type="thin-vertical"
			class="d-flex flex-1 min-width-0"
			on:value_change={(e) => {
				changePosition(e, 'slat_poses');
			}}
		/>
	</div>
{:else}
	<Slider
		a11yProps={a11yPropsPosition}
		id="slider-jdbi8oc"
		class={'flex-full-height mb-l'}
		title={$format('label_position')}
		{value}
		{disabled}
		type={'vertical'}
		on:value_change={async (e) => await changePosition(e)}
		{isLoading}
	/>
{/if}
